 
/* eslint-disable prefer-custom-date-types/prefer-custom-date-types */
import { DateTime } from "./date-time";
import { UTCDate } from "./utc-date";

// correct here to allow native Date objects to pass through without conversion, since they are already in the desired format for Excel export
// eslint-disable-next-line @typescript-eslint/no-restricted-imports
import { isDate } from "lodash";

/**
 * Converts a DateTime to a Date object where the local time components in the specified timezone
 * are treated as UTC components. This is useful for Excel which doesn't support timezones.
 *
 * Example: DateTime 2025-11-01T15:00:00+01:00 in Europe/Berlin
 *          -> Date 2025-11-01T15:00:00Z (the local time 15:00 becomes UTC 15:00)
 *
 * @param dateTime The DateTime to convert
 * @param targetZoneId The IANA timezone to use for extracting local components (e.g., "Europe/Berlin")
 */
function convertDateTimeForExcel(dateTime: DateTime, targetZoneId: string): Date {
  const localComponents = dateTime.withZone(targetZoneId).getLocalComponents();
  return new Date(
    Date.UTC(
      localComponents.year,
      localComponents.monthIndex,
      localComponents.day,
      localComponents.hour,
      localComponents.minute,
      localComponents.second,
      localComponents.millisecond
    )
  );
}

/**
 * Converts a UTCDate to a Date object at UTC midnight.
 * UTCDate "2025-11-01" -> Date 2025-11-01T00:00:00Z
 *
 * @param utcDate The UTCDate to convert
 */
function convertUtcDateForExcel(utcDate: UTCDate): Date {
  return utcDate.toDate();
}

/**
 * Creates a deep proxy that automatically converts DateTime and UTCDate properties to native Date objects
 * suitable for Excel export. Excel doesn't support timezones, so DateTime values are converted by
 * extracting their local time components in the specified timezone and treating them as UTC.
 *
 * - UTCDate "2025-11-01" -> Date 2025-11-01T00:00:00Z
 * - DateTime 2025-11-01T15:00:00+01:00 (in Europe/Berlin) -> Date 2025-11-01T15:00:00Z
 *
 * @param obj The object to wrap in a proxy
 * @param targetZoneId The IANA timezone to use for DateTime conversion (e.g., "Europe/Berlin")
 */
export function createExcelDateConvertingProxy<T>(obj: T, targetZoneId: string): T {
  if (obj === null || obj === undefined) {
    return obj;
  }

  if (typeof obj !== "object") {
    return obj;
  }

  if (obj instanceof DateTime) {
    return convertDateTimeForExcel(obj, targetZoneId) as any;
  }

  if (obj instanceof UTCDate) {
    return convertUtcDateForExcel(obj) as any;
  }

  if (isDate(obj)) {
    return obj;
  }

  if (typeof obj === "function") {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map((item) => createExcelDateConvertingProxy(item, targetZoneId)) as any;
  }

  return new Proxy(obj, {
    get(target: any, prop: string | symbol, receiver: any): any {
      const value = Reflect.get(target, prop, receiver);

      if (typeof value === "function") {
        return value.bind(target);
      }

      if (value === null || value === undefined) {
        return value;
      }

      if (value instanceof DateTime) {
        return convertDateTimeForExcel(value, targetZoneId);
      }

      if (value instanceof UTCDate) {
        return convertUtcDateForExcel(value);
      }

      if (typeof value === "object") {
        return createExcelDateConvertingProxy(value, targetZoneId);
      }

      return value;
    },
  });
}

/**
 * Creates a deep proxy that automatically converts DateTime and UTCDate properties to native Date objects
 * by calling their toDate() method when accessed.
 */
export function createDateConvertingProxy<T>(obj: T): T {
  if (obj === null || obj === undefined) {
    return obj;
  }

  if (typeof obj !== "object") {
    return obj;
  }

  if (obj instanceof DateTime || obj instanceof UTCDate) {
    return obj.toDate() as any;
  }

  if (isDate(obj)) {
    return obj;
  }

  if (typeof obj === "function") {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map((item) => createDateConvertingProxy(item)) as any;
  }

  return new Proxy(obj, {
    get(target: any, prop: string | symbol, receiver: any): any {
      const value = Reflect.get(target, prop, receiver);

      if (typeof value === "function") {
        return value.bind(target);
      }

      if (value === null || value === undefined) {
        return value;
      }

      if (value instanceof DateTime || value instanceof UTCDate) {
        return value.toDate();
      }

      if (typeof value === "object") {
        return createDateConvertingProxy(value);
      }

      return value;
    },
  });
}

/**
 * Creates a deep proxy for process evaluation data objects that automatically converts
 * DateTime and UTCDate properties to native Date objects.
 * This allows expressions to work with standard Date objects instead of custom date types.
 */
export function createProcessDataProxy<T extends Record<string, any>>(data: T): T {
  if (!data) {
    return data;
  }

  return new Proxy(data, {
    get(target: T, prop: string | symbol, receiver: any): any {
      const value = Reflect.get(target, prop, receiver);

      if (typeof value === "function") {
        return (value as any).bind(target);
      }

      return createDateConvertingProxy(value);
    },
  });
}
